import { useState, useEffect, useRef } from 'react';

import './wallboard-statusoverview.css';

import {
    Widget,
    LoadingIndicator,
    Table,
} from '../../../components/components';

import { useInterval } from '../../../interval';
import { calcDuration } from '../puzzel-queue';
import { loadWallboardAgentDetailsForQueues, loadWallboardQueues } from '../wallboards';

function parseQueue(queue) {
    return {
        name: queue.description,
        size: queue.queueSize,
        waitTime: calcDuration(queue.waitTimeMaxSeconds),
    };
}

function useQueues(refresh, customerKeys, limit) {
    const [queues, setQueues] = useState([]);
    const [tokens, setTokens] = useState({});

    const load = useRef(async () => {
        const queues = await loadWallboardQueues(customerKeys, tokens, setTokens);

        const filteredQueues = queues
            .filter(queue => queue.queueSize > 0)
            .sort((a, b) => {
                // Multiple properties are used in sorting to garantee stability
                // ergo: Chrome can't use its internal in-place QuickSort functionality
    
                if (a.waitTimeMaxSeconds === b.waitTimeMaxSeconds) {
                    return a.queueSize < b.queueSize ? -1 : 1;
                }
                return a.waitTimeMaxSeconds > b.waitTimeMaxSeconds ? -1 : 1;
            })
            .slice(0, limit);

        const parsedQueues = filteredQueues.map(parseQueue);

        setQueues(parsedQueues);
    });

    useInterval(() => {
        load.current();
    }, refresh * 1000);

    useEffect(() => {
        load.current();
    }, []);

    return queues;
}

const lunchStatuses = [
    "Lunch",
    "Lunsj",
];
const breakStatuses = [
    "Break",
    "Away from desk",
    "Paus",
];

function parseStatusDescription(agent) {
    if (agent.contactCentreStatus !== "Pause") {
        return "";
    }

    if (lunchStatuses.includes(agent.statusDescription)) {
        return "lunch";
    }

    if (breakStatuses.includes(agent.statusDescription)) {
        return "break";
    }

    return "unknown";
}

function parseAgents(agents, limit) {
    const agentIds = Object.getOwnPropertyNames(agents);
    const agentDetails = agentIds.map(id => agents[id]);

    const parsedAgents = agentDetails
        .sort((a, b) => {
            // Multiple properties are used in sorting to garantee stability
            // ergo: Chrome can't use its internal in-place QuickSort functionality

            if (a.timeInStatus === b.timeInStatus) {
                return a.userId < b.userId ? -1 : 1;
            }
            return a.timeInStatus > b.timeInStatus ? -1 : 1;
        })
        .slice(0, limit)
        .map(agent => {
            return {
                name: `${agent.firstName} ${agent.lastName}`,
                status: parseStatusDescription(agent),
                duration: calcDuration(agent.timeInStatus),
            };
        });

    return [agentIds.length, parsedAgents];
}

function useAgents(refresh, customerKeys, limit) {
    const [availableNumber, setAvailableNumber] = useState(0);
    const [pauseNumber, setPauseNumber] = useState(0);
    const [unavailableNumber, setUnavailableNumber] = useState(0);
    const [callNumber, setCallNumber] = useState(0);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [pauseAgents, setPauseAgents] = useState([]);
    const [unavailableAgents, setUnavailableAgents] = useState([]);
    const [callAgents, setCallAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tokens, setTokens] = useState({});
    const [hasLoadedValues, setHasLoadedValues] = useState(false);

    const load = useRef(async loading => {
        if (loading) {
            return;
        }

        setLoading(true);

        const [available, connected, unavailable, pause] = await loadWallboardAgentDetailsForQueues(customerKeys, tokens, setTokens);

        const [availableNumber, availableList] = parseAgents(available, limit);
        const [pauseNumber, pauseList] = parseAgents(pause, limit);
        const [connectedNumber, connectedList] = parseAgents(connected, limit);
        const [unavailableNumber, unavailableList] = parseAgents(unavailable, limit);

        setAvailableNumber(availableNumber);
        setAvailableAgents(availableList);
        setPauseNumber(pauseNumber);
        setPauseAgents(pauseList);
        setCallNumber(connectedNumber);
        setCallAgents(connectedList);
        setUnavailableNumber(unavailableNumber);
        setUnavailableAgents(unavailableList);
        setLoading(false);
        setHasLoadedValues(true);
    });

    useInterval(() => {
        load.current(loading);
    }, refresh * 1000);

    useEffect(() => {
        load.current(false);
    }, []);

    return [hasLoadedValues, availableNumber, availableAgents, pauseNumber, pauseAgents, unavailableNumber, unavailableAgents, callNumber, callAgents];
}

function QueueList({queues}) {
    return (
        <Table class="queues-list no-margins">
            <thead>
                <tr>
                    <th>Queue</th>
                    <th>Calls waiting</th>
                    <th>Max wait time</th>
                </tr>
            </thead>
            <tbody>
                {queues.map((queue, index) => 
                    <tr key={index}>
                        <td>
                            {queue.name}
                        </td>
                        <td>
                            {queue.size}
                        </td>
                        <td>
                            {queue.waitTime}
                        </td>
                    </tr>
                )}
                {queues.length === 0 &&
                    <tr>
                        <td colSpan="3">
                            No waiting calls in queues 
                        </td>
                    </tr>
                }
            </tbody>
        </Table>
    );
}

function AgentList({agents, useStatusIncidator}) {
    if (agents.length === 0) {
        return (
            <p>No agents in status</p>
        );
    }

    return agents.map((agent, index) => 
        <p key={index} className="agent-list">
            <span>
                {useStatusIncidator &&
                    <StatusIndicator variant={agent.status} />
                }
                {agent.name}
            </span>
            <span>{agent.duration}</span>
        </p>
    );
}

export function StatusOverviewWallboard({ wallboard, refresh }) {
    const queueLimit = wallboard.settings["max-queues"] || 5;
    const queues = useQueues(refresh, wallboard.customerKeys, queueLimit);

    const agentLimit = wallboard.settings["max-agents"] || 10;
    const [hasLoaded, availableNumber, availableAgents, pauseNumber, pauseAgents, unavailableNumber, unavailableAgents, callNumber, callAgents] = useAgents(refresh, wallboard.customerKeys, agentLimit);

    return (
        <div className="statusoverview-container">
            <QueueList queues={queues} />

            <Widget class="available-number" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <p className="small-number centered">{availableNumber}</p>
                    <p className="text centered">Available</p>
                </Loader>
            </Widget>
            <Widget class="pause-number" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <p className="small-number centered">{pauseNumber}</p>
                    <p className="text centered flex"><StatusIndicator variant="lunch" /> Lunch <StatusIndicator variant="break" /> Break <StatusIndicator /> Pause</p>
                </Loader>
            </Widget>
            <Widget class="unavailable-number" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <p className="small-number centered">{unavailableNumber}</p>
                    <p className="text centered">Not available</p>
                </Loader>
            </Widget>
            <Widget class="call-number" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <p className="small-number centered">{callNumber}</p>
                    <p className="text centered">In a call</p>
                </Loader>
            </Widget>

            <Widget class="available-list" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <AgentList agents={availableAgents} />
                </Loader>
            </Widget>
            <Widget class="pause-list" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <AgentList agents={pauseAgents} useStatusIncidator={true} />
                </Loader>
            </Widget>
            <Widget class="unavailable-list" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <AgentList agents={unavailableAgents} />
                </Loader>
            </Widget>
            <Widget class="call-list" noMargins="true">
                <Loader isLoading={!hasLoaded}>
                    <AgentList agents={callAgents} />
                </Loader>
            </Widget>
        </div>
    );
}

function Loader(props) {
    if (props.isLoading) {
        return (
            <LoadingIndicator />
        );
    }

    return props.children;
}

function StatusIndicator({variant}) {
    return (
        <span className={`status-indicator ${variant}`}></span>
    );
}